import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePaywall } from "@/components/PaywallContext";
import { getTodayStr, formatDateLocal } from "@/lib/utils";
import { 
  Wallet, 
  Receipt, 
  CreditCard, 
  ArrowRight, 
  Check, 
  Plus,
  X,
  Sparkles,
  ChevronRight
} from "lucide-react";

// Bill categories for quick add
const BILL_CATEGORIES = [
  { id: "housing", label: "Rent/Mortgage", icon: "🏠", amount: null },
  { id: "utilities", label: "Utilities", icon: "💡", amount: null },
  { id: "food", label: "Groceries", icon: "🛒", amount: null },
  { id: "transport", label: "Transport", icon: "🚗", amount: null },
  { id: "subscriptions", label: "Subscriptions", icon: "📱", amount: null },
  { id: "insurance", label: "Insurance", icon: "🛡️", amount: null }
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { triggerPostOnboarding } = usePaywall();
  const [step, setStep] = useState(0); // 0=welcome, 1=income, 2=bills, 3=debts, 4=complete
  const [loading, setLoading] = useState(false);
  
  // Starting balance state - for Safe to Spend calculation
  const [startingBalance, setStartingBalance] = useState("");
  
  // Income state - include next_pay_date for accurate Safe to Spend calculation
  const [incomeData, setIncomeData] = useState({
    name: "Salary",
    amount: "",
    frequency: "monthly",
    next_pay_date: "" // User must enter their actual next pay date
  });
  
  // Bills state - include due_date for accurate calendar placement
  const [bills, setBills] = useState([]);
  const [showAddBill, setShowAddBill] = useState(false);
  const [newBill, setNewBill] = useState({
    name: "",
    amount: "",
    category: "other",
    frequency: "monthly",
    due_date: "" // User specifies when the bill is next due
  });
  
  // Debts state
  const [debts, setDebts] = useState([]);
  const [showAddDebt, setShowAddDebt] = useState(false);
  const [newDebt, setNewDebt] = useState({
    name: "",
    amount: "",
    totalDebt: "",
    interestRate: ""
  });

  // Track onboarding started
  useEffect(() => {
    trackEvent("onboarding_started");
  }, []);

  const trackEvent = async (eventName, metadata = {}) => {
    try {
      await axios.post(`${API}/track/event`, {
        event_name: eventName,
        metadata
      }, { withCredentials: true });
    } catch (error) {
      console.error("Tracking error:", error);
    }
  };

  // Step 1: Save Income and Starting Balance
  const handleSaveIncome = async () => {
    // Validate starting balance (can be 0, but must be a valid number)
    const balanceValue = parseFloat(startingBalance);
    if (startingBalance === "" || isNaN(balanceValue) || balanceValue < 0) {
      toast.error("Please enter your current available balance (can be £0)");
      return;
    }
    
    if (!incomeData.amount || parseFloat(incomeData.amount) <= 0) {
      toast.error("Please enter your income amount");
      return;
    }
    
    if (!incomeData.next_pay_date) {
      toast.error("Please enter when you're next getting paid");
      return;
    }
    
    setLoading(true);
    try {
      // Save starting balance to user profile first (use PATCH /users/settings, not PUT /auth/me)
      await axios.patch(`${API}/users/settings`, {
        starting_balance: balanceValue
      }, { withCredentials: true });
      
      // Save income with user-provided next_pay_date
      await axios.post(`${API}/income`, {
        name: incomeData.name || "Salary",
        amount: parseFloat(incomeData.amount),
        frequency: incomeData.frequency,
        next_pay_date: incomeData.next_pay_date
      }, { withCredentials: true });
      
      await trackEvent("income_added", { 
        amount: parseFloat(incomeData.amount), 
        frequency: incomeData.frequency,
        starting_balance: balanceValue 
      });
      toast.success("Income added!");
      setStep(2);
    } catch (error) {
      toast.error("Failed to save income");
    } finally {
      setLoading(false);
    }
  };

  // Quick add bill from category
  const handleQuickAddBill = (category) => {
    const existingIdx = bills.findIndex(b => b.category === category.id);
    if (existingIdx >= 0) {
      // Remove if already added
      setBills(bills.filter((_, idx) => idx !== existingIdx));
    } else {
      // Show input for this category
      setNewBill({
        name: category.label,
        amount: "",
        category: category.id,
        frequency: "monthly"
      });
      setShowAddBill(true);
    }
  };

  // Add custom bill
  const handleAddBill = () => {
    if (!newBill.amount || parseFloat(newBill.amount) <= 0) {
      toast.error("Please enter an amount");
      return;
    }
    
    if (!newBill.due_date) {
      toast.error("Please enter when this bill is next due");
      return;
    }
    
    setBills([...bills, {
      name: newBill.name || "Bill",
      amount: parseFloat(newBill.amount),
      category: newBill.category,
      frequency: newBill.frequency,
      due_date: newBill.due_date // Store user-provided due date
    }]);
    
    setNewBill({ name: "", amount: "", category: "other", frequency: "monthly", due_date: "" });
    setShowAddBill(false);
  };

  // Save all bills and proceed
  const handleSaveBills = async () => {
    // Allow proceeding without bills (skip option)
    if (bills.length === 0) {
      setStep(3);
      return;
    }
    
    setLoading(true);
    try {
      for (const bill of bills) {
        await axios.post(`${API}/bills`, {
          name: bill.name,
          amount: bill.amount,
          category: bill.category,
          frequency: bill.frequency,
          start_date: bill.due_date, // Use user-provided due date
          is_debt: false
        }, { withCredentials: true });
      }
      
      await trackEvent("bills_added", { count: bills.length, total: bills.reduce((sum, b) => sum + b.amount, 0) });
      toast.success(`${bills.length} bill(s) added!`);
      setStep(3);
    } catch (error) {
      toast.error("Failed to save bills");
    } finally {
      setLoading(false);
    }
  };

  // Add debt
  const handleAddDebt = () => {
    if (!newDebt.amount || !newDebt.totalDebt) {
      toast.error("Please enter payment amount and total debt");
      return;
    }
    
    setDebts([...debts, {
      name: newDebt.name || "Debt",
      amount: parseFloat(newDebt.amount),
      totalDebt: parseFloat(newDebt.totalDebt),
      interestRate: parseFloat(newDebt.interestRate) || 0
    }]);
    
    setNewDebt({ name: "", amount: "", totalDebt: "", interestRate: "" });
    setShowAddDebt(false);
  };

  // Complete onboarding
  const handleComplete = async () => {
    setLoading(true);
    try {
      const today = getTodayStr();
      
      // Save debts if any
      for (const debt of debts) {
        await axios.post(`${API}/bills`, {
          name: debt.name,
          amount: debt.amount,
          category: "debt",
          frequency: "monthly",
          start_date: today,
          is_debt: true,
          total_debt: debt.totalDebt,
          interest_rate: debt.interestRate
        }, { withCredentials: true });
      }
      
      // Mark onboarding complete
      await axios.put(`${API}/users/settings`, {
        onboarding_completed: true
      }, { withCredentials: true });
      
      await trackEvent("onboarding_completed", { 
        debts_count: debts.length,
        total_debt: debts.reduce((sum, d) => sum + d.totalDebt, 0)
      });
      
      updateUser({ onboarding_completed: true });
      setStep(4);
      
      // Redirect after showing completion, then show paywall
      setTimeout(() => {
        navigate("/calendar");
        // Trigger paywall after user sees the calendar
        setTimeout(() => {
          triggerPostOnboarding();
        }, 1500);
      }, 2000);
      
    } catch (error) {
      toast.error("Failed to complete setup");
    } finally {
      setLoading(false);
    }
  };

  // Skip debts
  const handleSkipDebts = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/users/settings`, {
        onboarding_completed: true
      }, { withCredentials: true });
      
      await trackEvent("onboarding_completed", { debts_count: 0, skipped_debts: true });
      updateUser({ onboarding_completed: true });
      setStep(4);
      
      setTimeout(() => {
        navigate("/calendar");
        // Trigger paywall after user sees the calendar
        setTimeout(() => {
          triggerPostOnboarding();
        }, 1500);
      }, 2000);
    } catch (error) {
      toast.error("Failed to complete setup");
    } finally {
      setLoading(false);
    }
  };

  // Progress indicator - render directly instead of nested component
  const renderProgressIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
            step >= s 
              ? 'bg-emerald-500 text-white' 
              : 'bg-slate-700 text-slate-400'
          }`}>
            {step > s ? <Check className="w-4 h-4" /> : s}
          </div>
          {s < 3 && (
            <div className={`w-12 h-1 mx-1 rounded transition-all ${
              step > s ? 'bg-emerald-500' : 'bg-slate-700'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        
        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="text-center animate-in fade-in duration-500" data-testid="onboarding-welcome">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-3">
              Let&apos;s get you in control of your money
            </h1>
            <p className="text-slate-400 mb-8 max-w-sm mx-auto">
              In just 2 minutes, we&apos;ll set up your financial overview and show you exactly where you stand.
            </p>
            
            <Button 
              onClick={() => setStep(1)}
              className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-xl"
              data-testid="start-onboarding-btn"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 1: Income */}
        {step === 1 && (
          <div className="animate-in slide-in-from-right duration-300" data-testid="onboarding-income">
            {renderProgressIndicator()}
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Add Your Income</h2>
                  <p className="text-sm text-slate-400">How much do you earn?</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Starting Balance - Required for accurate Safe to Spend */}
                <div className="p-4 bg-slate-900/80 rounded-xl border border-slate-600">
                  <Label className="text-white font-medium">How much money do you have available right now?</Label>
                  <Input
                    type="number"
                    value={startingBalance}
                    onChange={(e) => setStartingBalance(e.target.value)}
                    placeholder="e.g., 500"
                    className="bg-slate-800 border-slate-600 text-white mt-2 text-lg"
                    data-testid="starting-balance-input"
                    min="0"
                    step="0.01"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Enter the amount currently available to cover your upcoming spending and bills. You can update this anytime from your calendar.
                  </p>
                </div>
                
                <div className="border-t border-slate-700 pt-4">
                  <h3 className="text-sm font-medium text-slate-400 mb-3">Your Income</h3>
                  
                  <div>
                    <Label className="text-slate-300">Income Name</Label>
                    <Input
                      value={incomeData.name}
                      onChange={(e) => setIncomeData({ ...incomeData, name: e.target.value })}
                      placeholder="e.g., Salary, Freelance"
                      className="bg-slate-900/50 border-slate-600 text-white mt-1"
                      data-testid="income-name-input"
                    />
                  </div>
                  
                  <div className="mt-3">
                    <Label className="text-slate-300">Amount (£)</Label>
                    <Input
                      type="number"
                      value={incomeData.amount}
                      onChange={(e) => setIncomeData({ ...incomeData, amount: e.target.value })}
                      placeholder="e.g., 2500"
                      className="bg-slate-900/50 border-slate-600 text-white mt-1 text-lg"
                      data-testid="income-amount-input"
                    />
                  </div>
                  
                  <div className="mt-3">
                    <Label className="text-slate-300">Frequency</Label>
                    <Select 
                      value={incomeData.frequency} 
                      onValueChange={(v) => setIncomeData({ ...incomeData, frequency: v })}
                    >
                      <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                        <SelectItem value="fourweekly">Every 4 weeks (28 days)</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="mt-3">
                    <Label className="text-slate-300">When are you next getting paid?</Label>
                    <Input
                      type="date"
                      value={incomeData.next_pay_date}
                      onChange={(e) => setIncomeData({ ...incomeData, next_pay_date: e.target.value })}
                      className="bg-slate-900/50 border-slate-600 text-white mt-1"
                      data-testid="income-next-pay-date-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">This helps us calculate your Safe to Spend accurately</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handleSaveIncome}
                disabled={loading}
                className="w-full mt-6 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-xl"
                data-testid="save-income-btn"
              >
                {loading ? "Saving..." : "Continue"}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Bills */}
        {step === 2 && (
          <div className="animate-in slide-in-from-right duration-300" data-testid="onboarding-bills">
            {renderProgressIndicator()}
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Add Your Bills</h2>
                  <p className="text-sm text-slate-400">What are your regular expenses?</p>
                </div>
              </div>
              
              {/* Quick add categories */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {BILL_CATEGORIES.map((cat) => {
                  const isAdded = bills.some(b => b.category === cat.id);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleQuickAddBill(cat)}
                      data-testid={`bill-category-${cat.id}`}
                      className={`p-3 rounded-xl text-center transition-all ${
                        isAdded 
                          ? 'bg-emerald-500/20 border-2 border-emerald-500' 
                          : 'bg-slate-700/50 border border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <span className="text-2xl">{cat.icon}</span>
                      <p className="text-xs text-slate-300 mt-1">{cat.label}</p>
                      {isAdded && (
                        <p className="text-xs text-emerald-400 font-semibold">
                          £{bills.find(b => b.category === cat.id)?.amount}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Added bills list */}
              {bills.length > 0 && (
                <div className="mb-4 p-3 bg-slate-900/50 rounded-xl">
                  <p className="text-xs text-slate-400 mb-2">Added bills:</p>
                  <div className="space-y-2">
                    {bills.map((bill, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div>
                          <span className="text-sm text-slate-300">{bill.name}</span>
                          <span className="text-xs text-slate-500 ml-2">Due: {bill.due_date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-white">£{bill.amount}</span>
                          <button 
                            onClick={() => setBills(bills.filter((_, i) => i !== idx))}
                            className="text-slate-500 hover:text-red-400"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-slate-700 flex justify-between">
                      <span className="text-sm text-slate-400">Monthly total</span>
                      <span className="font-mono font-semibold text-white">
                        £{bills.reduce((sum, b) => sum + b.amount, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Add custom bill modal */}
              {showAddBill && (
                <div className="mb-4 p-4 bg-slate-900/80 rounded-xl border border-slate-600">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-white">{newBill.name || "Add Bill"}</span>
                    <button onClick={() => setShowAddBill(false)} className="text-slate-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={newBill.amount}
                        onChange={(e) => setNewBill({ ...newBill, amount: e.target.value })}
                        placeholder="Amount (£)"
                        className="bg-slate-800 border-slate-600 text-white flex-1"
                        autoFocus
                        data-testid="new-bill-amount-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">When is this bill next due?</Label>
                      <Input
                        type="date"
                        value={newBill.due_date}
                        onChange={(e) => setNewBill({ ...newBill, due_date: e.target.value })}
                        className="bg-slate-800 border-slate-600 text-white mt-1"
                        data-testid="new-bill-due-date-input"
                      />
                    </div>
                    <Button 
                      onClick={handleAddBill}
                      data-testid="confirm-add-bill-btn"
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Bill
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Add other bill button */}
              {!showAddBill && (
                <button
                  onClick={() => {
                    setNewBill({ name: "", amount: "", category: "other", frequency: "monthly", due_date: "" });
                    setShowAddBill(true);
                  }}
                  className="w-full p-3 rounded-xl border-2 border-dashed border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300 transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add other expense
                </button>
              )}
              
              <Button 
                onClick={handleSaveBills}
                disabled={loading}
                className="w-full mt-6 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-xl disabled:opacity-50"
                data-testid="save-bills-btn"
              >
                {loading ? "Saving..." : bills.length === 0 ? "Skip for now" : "Continue"}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Debts (Optional) */}
        {step === 3 && (
          <div className="animate-in slide-in-from-right duration-300" data-testid="onboarding-debts">
            {renderProgressIndicator()}
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Any Debts to Track?</h2>
                  <p className="text-sm text-slate-400">Optional: Add loans, credit cards, etc.</p>
                </div>
              </div>
              
              {/* Added debts list */}
              {debts.length > 0 && (
                <div className="mb-4 space-y-2">
                  {debts.map((debt, idx) => (
                    <div key={idx} className="p-3 bg-slate-900/50 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-white">{debt.name}</p>
                        <p className="text-xs text-slate-400">
                          £{debt.amount}/mo • Total: £{debt.totalDebt}
                        </p>
                      </div>
                      <button 
                        onClick={() => setDebts(debts.filter((_, i) => i !== idx))}
                        className="text-slate-500 hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add debt form */}
              {showAddDebt ? (
                <div className="mb-4 p-4 bg-slate-900/80 rounded-xl border border-slate-600 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-white">Add Debt</span>
                    <button onClick={() => setShowAddDebt(false)} className="text-slate-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <Input
                    value={newDebt.name}
                    onChange={(e) => setNewDebt({ ...newDebt, name: e.target.value })}
                    placeholder="e.g., Credit Card, Car Loan"
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      value={newDebt.amount}
                      onChange={(e) => setNewDebt({ ...newDebt, amount: e.target.value })}
                      placeholder="Monthly payment (£)"
                      className="bg-slate-800 border-slate-600 text-white"
                    />
                    <Input
                      type="number"
                      value={newDebt.totalDebt}
                      onChange={(e) => setNewDebt({ ...newDebt, totalDebt: e.target.value })}
                      placeholder="Total owed (£)"
                      className="bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                  
                  <Input
                    type="number"
                    value={newDebt.interestRate}
                    onChange={(e) => setNewDebt({ ...newDebt, interestRate: e.target.value })}
                    placeholder="Interest rate % (optional)"
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                  
                  <Button 
                    onClick={handleAddDebt}
                    className="w-full bg-purple-500 hover:bg-purple-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Debt
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddDebt(true)}
                  className="w-full p-4 rounded-xl border-2 border-dashed border-slate-600 text-slate-400 hover:border-purple-500/50 hover:text-purple-400 transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add a debt
                </button>
              )}
              
              <div className="flex gap-3 mt-6">
                <Button 
                  onClick={handleSkipDebts}
                  disabled={loading}
                  variant="outline"
                  className="flex-1 h-12 border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Skip for now
                </Button>
                <Button 
                  onClick={handleComplete}
                  disabled={loading}
                  className="flex-1 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-xl"
                  data-testid="complete-onboarding-btn"
                >
                  {loading ? "Finishing..." : "Complete Setup"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Completion */}
        {step === 4 && (
          <div className="text-center animate-in zoom-in duration-500" data-testid="onboarding-complete">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-3">
              You&apos;re all set! 🎉
            </h1>
            <p className="text-slate-400 mb-6">
              Let&apos;s see your financial overview...
            </p>
            
            <div className="animate-pulse">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
